import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form';
import { makeStyles, TextField } from '@material-ui/core';
import { Modal } from '@components/Customized';
import { CapaErrorCode } from '@generated/graphql';
import { ProcessEventResult, processEvents } from '@utils/customEvent';
import { colorSet } from '@components/Provider';
import usePaymentModal from './usePaymentModal';
import { PaymentModalProps } from './types';
import { throttle } from 'lodash';
import { formatDate } from '@utils/formatter';

const ExtensionPaymentDateModal: React.FunctionComponent<
  PaymentModalProps & {
    purchaseOrderId: string;
    rfqId?: string;
    quotationPartnerId?: string;
    expectedPaymentApproveDate: string;
    paymentApproveDateAfterExtended: string;
  }
> = ({
  rfqId = '',
  quotationPartnerId = '',
  purchaseOrderId,
  openButton,
  expectedPaymentApproveDate,
  paymentApproveDateAfterExtended
}) => {
  const {
    open,
    onOpen,
    onClose,
    loading,
    setLoading,
    enqueueSnackbar
  } = usePaymentModal();
  const classes = useStyles();
  const methods = useForm<{ reason: string }>();
  const { control, handleSubmit } = methods;

  const onSubmit = throttle(
    handleSubmit(
      async (data) => {
        const reason = data.reason.trim();
        if (!reason) {
          enqueueSnackbar('구매확정일 연장 사유를 입력해주세요');
          return;
        }
        setLoading(true);
        try {
          const result = await processEvents.자동구매확정일연장({
            rfqId,
            quotationPartnerId,
            purchaseOrderId,
            extendReason: data.reason
          });

          if (result.result === CapaErrorCode.Success) {
            enqueueSnackbar('자동구매확정일이 연장되었습니다.');
            onClose();
          } else {
            throw new Error(result.result);
          }
        } catch (e) {
          switch ((e as ProcessEventResult)?.result) {
            case CapaErrorCode.ShippingNotFound:
          }

          enqueueSnackbar(
            '자동구매확정일 연장처리에 실패했습니다. 다시 시도해주세요'
          );
        } finally {
          setLoading(false);
        }
      },
      () => {
        enqueueSnackbar('구매확정일 연장 사유를 입력해주세요');
      }
    ),
    1000
  );

  return (
    <>
      {openButton(onOpen)}
      <Modal open={open} onClose={onClose}>
        <Modal.CloseButton onClose={onClose} />
        <Modal.Title>자동 구매확정일 연장</Modal.Title>

        <Modal.ContentSection>
          <Modal.Text variant="body1" color="textPrimary" gutterBottoms={12}>
            파트너에게 먼저 확인 후 자동 구매확정일을 연장해주세요.
          </Modal.Text>
          <Modal.Text variant="body2" color="textSecondary" gutterBottoms={2}>
            구매확정 예정일&nbsp;&nbsp;
            <span className={classes.date}>
              {formatDate(expectedPaymentApproveDate, 'YYYY.MM.DD')}
            </span>
          </Modal.Text>
          <Modal.Text variant="body2" color="textSecondary" gutterBottoms={24}>
            구매확정일 연장 후&nbsp;&nbsp;
            <span className={classes.date}>
              {paymentApproveDateAfterExtended
                ? formatDate(paymentApproveDateAfterExtended, 'YYYY.MM.DD')
                : undefined}
            </span>
          </Modal.Text>
          <Modal.Text
            className={classes.text}
            variant="body1"
            color="textPrimary"
            fontWeight="500"
            gutterBottoms={12}
          >
            연장 사유 입력
            <FormProvider {...methods}>
              <WatchTextCount />
            </FormProvider>
          </Modal.Text>

          <Controller
            name="reason"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              maxLength: 500
            }}
            render={(renderProps) => (
              <TextField
                {...renderProps}
                placeholder="연장 상세 사유를 입력해주세요."
                multiline
                fullWidth
                variant="outlined"
                minRows={4}
                maxRows={4}
                onChange={(e) => {
                  if (e.target.value.trim().length > 500) {
                    return;
                  }
                  renderProps.onChange(e.target.value);
                }}
              />
            )}
          />
        </Modal.ContentSection>

        <Modal.ButtonSection>
          <Modal.Button color="primary" onClick={onSubmit} disabled={loading}>
            완료
          </Modal.Button>
        </Modal.ButtonSection>
      </Modal>
    </>
  );
};

const WatchTextCount = () => {
  const { control } = useFormContext();
  const reason = useWatch({ control, name: 'reason', defaultValue: '' });

  return <span>({reason.length}/500)</span>;
};

export default ExtensionPaymentDateModal;

const useStyles = makeStyles(
  (theme) => ({
    text: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 12,
      '& span': {
        fontWeight: 400
      }
    },
    date: {
      ...theme.typography.body2,
      fontWeight: 500,
      color: colorSet.primary500
    }
  }),
  { classNamePrefix: 'extension-paymentdate-modal' }
);
